import "./style.scss";
import { useState, useEffect, useRef, FC } from "react";
import ModalPortal from "@/src/components/ModalPortal/ModalPortal";
import CloseSvg from "@/src/svg/applyOnline/CloseSvg";
import DeleteDarkSvg from "@/src/svg/applyOnline/DeleteDarkSvg";

export type FileAttachmentField = {
  name: string;
  title: string;
};

export type FilesByFieldMap = Record<string, File[]>;

export interface IFileAttachmentModalProps {
  isActive: boolean;
  onClose: () => void;
  fields: FileAttachmentField[];
  onAttach: (filesMap: FilesByFieldMap) => void;
  selectedFilesMap?: FilesByFieldMap;
  title?: string;
  subtitle?: string;
  submitLabel?: string;
  /** Render into #modal-root (full viewport). Default: true */
  usePortal?: boolean;
}

const FormSummaryModal: FC<IFileAttachmentModalProps> = ({
  isActive,
  onClose,
  fields,
  onAttach,
  selectedFilesMap = {},
  title = "File attachment",
  subtitle = "Attach the necessary files for submission",
  submitLabel = "Attach Files →",
  usePortal = true,
}) => {
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [localFilesMap, setLocalFilesMap] = useState<FilesByFieldMap>({});

  useEffect(() => {
    if (isActive) {
      const clonedFilesMap: FilesByFieldMap = {};
      for (const { name } of fields) {
        clonedFilesMap[name] = [...(selectedFilesMap[name] || [])];
      }
      setLocalFilesMap(clonedFilesMap);
    }
  }, [isActive]);

  const handleFileChange = (name: string, files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files);
    const updated = [...(localFilesMap[name] || []), ...newFiles];
    setLocalFilesMap((prev) => ({ ...prev, [name]: updated }));
  };

  const handleDeleteFile = (name: string, index: number) => {
    const updated = (localFilesMap[name] || []).filter((_, i) => i !== index);
    setLocalFilesMap((prev) => ({ ...prev, [name]: updated }));
  };

  const handleAttachFiles = () => {
    onAttach(localFilesMap);
    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  if (!isActive) return null;

  const modal = (
    <div className="file-attachment-modal active">
      <div className="file-attachment-modal-bg" onClick={handleClose}></div>
      <div className="file-attachment-modal-body">
        <div className="file-attachment-modal-content">
          <div className="file-attachment-modal-content-header">
            <div className="file-attachment-modal-title">
              <p>{title}</p>
              <div className="file-attachment-modal-title-svg" onClick={handleClose}>
                <CloseSvg />
              </div>
            </div>

            <div className="file-attachment-modal-subtitle">
              <p>{subtitle}</p>
            </div>
          </div>
          <div className="file-attachment-modal-items">
            {fields.map(({ name, title: fieldTitle }) => (
              <div className="file-attachment-modal-item" key={name}>
                <p className="file-attachment-modal-item-title">{fieldTitle}</p>
                <div className="file-attachment-modal-item-content">
                  {(localFilesMap[name]?.length ?? 0) > 0 && (
                    <div className="file-attachment-modal-item-files">
                      {localFilesMap[name].map((file, index) => (
                        <div className="file-attachment-modal-item-file" key={file.name + index}>
                          <p>{file.name}</p>
                          <div className="file-attachment-modal-item-file-delete" onClick={() => handleDeleteFile(name, index)}>
                            <DeleteDarkSvg />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div
                    className="file-attachment-modal-item-button"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleFileChange(name, e.dataTransfer.files);
                    }}
                    onClick={() => fileInputRefs.current[name]?.click()}
                  >
                    <input
                      type="file"
                      multiple
                      ref={(el) => {
                        fileInputRefs.current[name] = el;
                      }}
                      style={{ display: "none" }}
                      onChange={(e) => {
                        handleFileChange(name, e.target.files);
                        e.target.value = "";
                      }}
                    />
                    <p className="file-attachment-modal-item-button-plus">+</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="file-attachment-modal-content-add-btn" onClick={handleAttachFiles}>
            {submitLabel}
          </div>
        </div>
      </div>
    </div>
  );

  if (usePortal) {
    return <ModalPortal>{modal}</ModalPortal>;
  }

  return modal;
};

export default FormSummaryModal;
